import type {
  FounderUpdateAsk,
  FounderUpdateIntelligence,
  FounderUpdateMetricExtraction,
  UpdateSentiment,
} from '../types';

const UPDATE_SENTIMENT_VALUES: UpdateSentiment[] = ['positive', 'neutral', 'negative', 'mixed'];
const METRIC_CONFIDENCE_VALUES: FounderUpdateMetricExtraction['confidence'][] = [
  'high',
  'medium',
  'low',
];
const HEALTH_SIGNAL_VALUES: FounderUpdateIntelligence['healthSignal'][] = [
  'improving',
  'stable',
  'deteriorating',
  'unclear',
];
const ASK_TYPES: FounderUpdateAsk['type'][] = [
  'intro',
  'hiring',
  'fundraising',
  'customer',
  'advice',
  'other',
];
const ASK_URGENCY_VALUES: FounderUpdateAsk['urgency'][] = ['low', 'medium', 'high'];
const RISK_SEVERITY_VALUES: FounderUpdateIntelligence['risks'][number]['severity'][] = [
  'low',
  'medium',
  'high',
];

function asString(v: unknown): string | undefined {
  return typeof v === 'string' ? v : undefined;
}

function asNumber(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function asStringArray(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  const items = v.filter((x): x is string => typeof x === 'string');
  return items.length ? items : undefined;
}

function isUpdateSentiment(v: unknown): v is UpdateSentiment {
  return typeof v === 'string' && UPDATE_SENTIMENT_VALUES.includes(v as UpdateSentiment);
}

function isMetricConfidence(v: unknown): v is FounderUpdateMetricExtraction['confidence'] {
  return typeof v === 'string' && METRIC_CONFIDENCE_VALUES.includes(v as FounderUpdateMetricExtraction['confidence']);
}

function isHealthSignal(v: unknown): v is FounderUpdateIntelligence['healthSignal'] {
  return typeof v === 'string' && HEALTH_SIGNAL_VALUES.includes(v as FounderUpdateIntelligence['healthSignal']);
}

function isAskType(v: unknown): v is FounderUpdateAsk['type'] {
  return typeof v === 'string' && ASK_TYPES.includes(v as FounderUpdateAsk['type']);
}

function isAskUrgency(v: unknown): v is FounderUpdateAsk['urgency'] {
  return typeof v === 'string' && ASK_URGENCY_VALUES.includes(v as FounderUpdateAsk['urgency']);
}

function isRiskSeverity(v: unknown): v is FounderUpdateIntelligence['risks'][number]['severity'] {
  return typeof v === 'string' && RISK_SEVERITY_VALUES.includes(v as FounderUpdateIntelligence['risks'][number]['severity']);
}

/** Stable short fingerprint of founder update source text for stale analysis detection. */
export function founderUpdateSourceFingerprint(text: string): string {
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function normalizeExtractedMetrics(raw: unknown): FounderUpdateMetricExtraction | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const x = raw as Record<string, unknown>;
  const confidence = x.confidence;
  const notes = asString(x.notes);
  if (!isMetricConfidence(confidence) || !notes) return undefined;

  const metrics: FounderUpdateMetricExtraction = { confidence, notes };
  const revenue = asNumber(x.revenue);
  if (revenue !== undefined) metrics.revenue = revenue;
  const burn = asNumber(x.burn);
  if (burn !== undefined) metrics.burn = burn;
  const remainingCash = asNumber(x.remainingCash);
  if (remainingCash !== undefined) metrics.remainingCash = remainingCash;
  const runwayMonths = asNumber(x.runwayMonths);
  if (runwayMonths !== undefined) metrics.runwayMonths = runwayMonths;
  const headcount = asNumber(x.headcount);
  if (headcount !== undefined) metrics.headcount = headcount;
  return metrics;
}

/** Parse and validate `updateIntelligence` from Firestore; returns undefined when invalid. */
export function normalizeFounderUpdateIntelligence(raw: unknown): FounderUpdateIntelligence | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const x = raw as Record<string, unknown>;

  const analyzedAt = asString(x.analyzedAt);
  const sourceTextFingerprint = asString(x.sourceTextFingerprint);
  const model = asString(x.model);
  const summary = asString(x.summary);
  const sentiment = x.sentiment;
  const healthSignal = x.healthSignal;
  const investorNotes = asString(x.investorNotes);

  if (
    !analyzedAt ||
    !sourceTextFingerprint ||
    !model ||
    !summary ||
    !isUpdateSentiment(sentiment) ||
    !isHealthSignal(healthSignal) ||
    !investorNotes
  ) {
    return undefined;
  }

  const extractedMetrics = normalizeExtractedMetrics(x.extractedMetrics);
  if (!extractedMetrics) return undefined;

  const whatChanged = asStringArray(x.whatChanged) ?? [];
  const milestones = asStringArray(x.milestones) ?? [];
  const followUpQuestions = asStringArray(x.followUpQuestions) ?? [];

  const risks = Array.isArray(x.risks)
    ? x.risks
        .filter((r) => r && typeof r === 'object')
        .map((r) => {
          const row = r as Record<string, unknown>;
          const description = asString(row.description);
          const severity = row.severity;
          const rationale = asString(row.rationale);
          if (!description || !isRiskSeverity(severity) || !rationale) return null;
          return { description, severity, rationale };
        })
        .filter((r): r is { description: string; severity: 'low' | 'medium' | 'high'; rationale: string } => r !== null)
    : [];

  const asks = Array.isArray(x.asks)
    ? x.asks
        .filter((a) => a && typeof a === 'object')
        .map((a) => {
          const row = a as Record<string, unknown>;
          const type = row.type;
          const description = asString(row.description);
          const urgency = row.urgency;
          if (!isAskType(type) || !description || !isAskUrgency(urgency)) return null;
          return { type, description, urgency };
        })
        .filter((a): a is FounderUpdateAsk => a !== null)
    : [];

  return {
    analyzedAt,
    sourceTextFingerprint,
    model,
    summary,
    sentiment,
    healthSignal,
    extractedMetrics,
    whatChanged,
    milestones,
    risks,
    asks,
    followUpQuestions,
    investorNotes,
  };
}
